<script lang="ts">
	import { modalOpen, closeModal } from '$lib/stores/modal';
	import { fade, scale } from 'svelte/transition';
	import { cubicOut } from 'svelte/easing';
	import posthog from 'posthog-js';

	let submitting = $state(false);
	let success = $state(false);
	let formEl: HTMLFormElement = $state()!;

	$effect(() => {
		if ($modalOpen) {
			posthog.capture('early_access_modal_opened');
		}
	});

	async function handleSubmit(e: SubmitEvent) {
		e.preventDefault();
		submitting = true;

		const formData = new FormData(formEl);
		const data = {
			name: formData.get('name'),
			email: formData.get('email'),
			company: formData.get('company'),
			fleet_size: formData.get('fleet_size'),
			message: formData.get('message'),
			timestamp: new Date().toISOString(),
			type: 'early_access_application'
		};

		try {
			const res = await fetch('https://crm.qdyn.au/contact', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				body: JSON.stringify(data)
			});
			if (!res.ok) throw new Error('Failed');
			const email = data.email as string;
			posthog.identify(email, {
				email,
				name: data.name as string,
				company: data.company as string
			});
			posthog.capture('early_access_form_submitted', {
				fleet_size: data.fleet_size,
				company: data.company
			});
			success = true;
			setTimeout(() => {
				closeModal();
				setTimeout(() => {
					success = false;
					formEl?.reset();
				}, 400);
			}, 2800);
		} catch (err) {
			posthog.captureException(err);
			alert('There was an issue submitting. Please email hello@admrl.co');
		} finally {
			submitting = false;
		}
	}

	function onKey(e: KeyboardEvent) {
		if (e.key === 'Escape') closeModal();
	}
</script>

<svelte:window onkeydown={onKey} />

{#if $modalOpen}
	<div
		role="dialog"
		aria-modal="true"
		tabindex="-1"
		class="fixed inset-0 z-[9999] flex items-center justify-center p-4"
		style="background: rgba(0,0,0,0.85); backdrop-filter: blur(12px);"
		onclick={(e) => {
			if (e.target === e.currentTarget) closeModal();
		}}
		onkeydown={() => {}}
		transition:fade={{ duration: 200 }}
	>
		<div
			class="flex max-w-[480px] flex-col gap-6 rounded-2xl border border-white/10 bg-[#0A0B14] p-10"
			transition:scale={{ duration: 300, start: 0.95, easing: cubicOut }}
		>
			<div class="flex items-center justify-between">
				<span
					class="badge-eyebrow inline-flex items-center rounded-md px-2.5 py-1 font-display text-[10px] font-medium uppercase tracking-[0.15em]"
				>
					Early Access
				</span>
				<button
					onclick={closeModal}
					class="flex h-8 w-8 items-center justify-center rounded-md text-2xl leading-none text-white/60 transition-colors hover:bg-white/10 hover:text-white"
					type="button"
					aria-label="Close"
				>
					&times;
				</button>
			</div>

			{#if !success}
				<div>
					<h3 class="mb-2 font-display text-2xl font-medium tracking-tight text-white">
						Apply for early access
					</h3>
					<p class="text-sm leading-relaxed text-white/60">
						Tell us about your fleet. We're working closely with a small group of design partners to
						shape Admiral. You'll get white-glove onboarding, direct access to the founding team,
						and preferential pricing.
					</p>
				</div>

				<form bind:this={formEl} onsubmit={handleSubmit} class="flex flex-col gap-3">
					<input
						type="text"
						name="name"
						required
						placeholder="Full name"
						class="focus:border-blue h-11 w-full rounded-md border border-white/10 bg-white/[0.03] px-4 font-display text-sm text-white placeholder-white/40 transition-colors focus:outline-none"
					/>
					<input
						type="email"
						name="email"
						required
						placeholder="Work email"
						class="focus:border-blue h-11 w-full rounded-md border border-white/10 bg-white/[0.03] px-4 font-display text-sm text-white placeholder-white/40 transition-colors focus:outline-none"
					/>
					<input
						type="text"
						name="company"
						required
						placeholder="Company"
						class="focus:border-blue h-11 w-full rounded-md border border-white/10 bg-white/[0.03] px-4 font-display text-sm text-white placeholder-white/40 transition-colors focus:outline-none"
					/>
					<select
						name="fleet_size"
						class="focus:border-blue h-11 w-full rounded-md border border-white/10 bg-white/[0.03] px-4 font-display text-sm text-white focus:outline-none"
					>
						<option value="" class="bg-[#0A0B14]">Current or planned fleet size</option>
						<option value="<100" class="bg-[#0A0B14]">Less than 100 devices</option>
						<option value="100-1000" class="bg-[#0A0B14]">100 – 1,000 devices</option>
						<option value="1000-10000" class="bg-[#0A0B14]">1,000 – 10,000 devices</option>
						<option value="10000+" class="bg-[#0A0B14]">10,000+ devices</option>
					</select>
					<textarea
						name="message"
						placeholder="Briefly: what are you building?"
						rows="3"
						class="focus:border-blue w-full resize-none rounded-md border border-white/10 bg-white/[0.03] px-4 py-3 font-display text-sm text-white placeholder-white/40 transition-colors focus:outline-none"
					></textarea>
					<div class="flex gap-3 pt-2">
						<button
							type="button"
							onclick={closeModal}
							class="btn-secondary h-11 flex-1 rounded-md font-display text-sm"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={submitting}
							class="btn-primary h-11 flex-1 rounded-md font-display text-sm font-medium disabled:opacity-50"
						>
							{submitting ? 'Submitting…' : 'Apply'}
						</button>
					</div>
				</form>
			{:else}
				<div class="py-4 text-center">
					<div class="mb-3 text-3xl text-[#10B981]">✓</div>
					<h4 class="mb-2 font-display text-lg font-medium text-white">Application received</h4>
					<p class="text-sm text-white/60">
						We review every application personally. Expect to hear from us within one business day.
					</p>
				</div>
			{/if}
		</div>
	</div>
{/if}
